/* RSC Health - shared test-center module (Phase 2).
   One source of truth for center data, service filtering, distance sort, and the
   selected-center state shared across the product ZIP check, Find a Test Center
   page, the site-wide strip, and the Patient & Lab step (spec §7 lab persistence).
   No PHI ever goes to a URL or analytics. window.RSC is the public API. */
(function () {
  'use strict';

  var LAB_KEY = 'rsc_selected_lab';       // shared with patient-lab.js
  var SOURCE_KEY = 'rsc_entry_source';

  // Source priority: the metaobject inline JSON (future Salesforce source of truth)
  // when it has entries, else the temporary window.__RSC_CENTERS__ blob asset.
  // Resolved LAZILY and memoized: this module can load (via the site-wide location
  // strip) before the page's blob/JSON script has run - deferred scripts execute in
  // DOM order - so binding the list once at init would lock in an empty array on any
  // page whose data is rendered after the strip. resolveCenters() re-checks until it
  // finds data, so load order no longer matters.
  var centers = [];
  function resolveCenters() {
    if (centers.length) return centers;
    try {
      var el = document.getElementById('rsc-centers-json');
      if (el) { var j = JSON.parse(el.textContent) || []; if (j.length) { centers = j; return centers; } }
    } catch (e) { /* fall through to blob */ }
    if (Array.isArray(window.__RSC_CENTERS__) && window.__RSC_CENTERS__.length) centers = window.__RSC_CENTERS__;
    return centers;
  }

  function haversine(a, b) {
    if (!a || !b || !b.lat || !b.lng) return Infinity;
    var R = 3958.8, toRad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * toRad, dLng = (b.lng - a.lng) * toRad;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function byService(service) {
    var all = resolveCenters();
    if (!service) return all.slice();
    return all.filter(function (c) { return (c.services || []).indexOf(service) > -1; });
  }

  function sortByGeo(list, geo) {
    if (!geo) return list;
    return list.slice().sort(function (a, b) { return haversine(geo, a) - haversine(geo, b); });
  }

  function textFilter(list, q) {
    q = (q || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter(function (c) {
      return [c.city, c.state, c.zip, c.name].join(' ').toLowerCase().indexOf(q) > -1;
    });
  }

  // Keep only centers within `miles` of geo, nearest first. Keyword is "near me".
  function withinRadius(list, geo, miles) {
    if (!geo) return list;
    return list
      .map(function (c) { return { c: c, d: haversine(geo, c) }; })
      .filter(function (x) { return x.d <= (miles || 40); })
      .sort(function (a, b) { return a.d - b.d; })
      .map(function (x) { return x.c; });
  }

  // Customer-facing name: brand only (address distinguishes locations). No "-" suffix.
  function brandDisplay(c) {
    var b = (c.brand || '').toLowerCase();
    if (b.indexOf('quest') > -1) return 'Quest Diagnostics';
    if (b.indexOf('labcorp') > -1) return 'LabCorp';
    return c.brand || (c.name || '').split(String.fromCharCode(8212))[0].trim();
  }

  function getSelected() {
    try { return JSON.parse(localStorage.getItem(LAB_KEY) || 'null'); } catch (e) { return null; }
  }

  // Persist selection to localStorage + cart attributes (so it survives to /checkout
  // and Salesforce) and notify listeners (strip, pickers).
  function setSelected(center, source) {
    try { localStorage.setItem(LAB_KEY, JSON.stringify(center)); } catch (e) {}
    if (source) { try { localStorage.setItem(SOURCE_KEY, source); } catch (e) {} }
    var addr = [center.street, center.city, center.state, center.zip].filter(Boolean).join(', ');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: {
        lab_id: center.id, lab_name: center.name, lab_address: addr,
        entry_source: source || 'find_test_center'
      } })
    }).catch(function () {});
    emit();
    return center;
  }

  function clearSelected() {
    try { localStorage.removeItem(LAB_KEY); } catch (e) {}
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { lab_id: '', lab_name: '', lab_address: '' } })
    }).catch(function () {});
    emit();
  }

  function emit() {
    document.dispatchEvent(new CustomEvent('rsc:center-changed', { detail: getSelected() }));
  }

  function geolocate(cb) {
    if (!navigator.geolocation) { cb(null); return; }
    navigator.geolocation.getCurrentPosition(
      function (p) { cb({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      function () { cb(null); },
      { timeout: 8000 }
    );
  }

  // Free, keyless ZIP → coordinates via zippopotam.us (CORS-enabled). The universal
  // fallback so distance search works with no Google Maps key. ZIP only, no PHI.
  function zipCentroid(zip, cb) {
    fetch('https://api.zippopotam.us/us/' + encodeURIComponent(zip))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var p = d && d.places && d.places[0];
        var lat = p ? parseFloat(p.latitude) : NaN, lng = p ? parseFloat(p.longitude) : NaN;
        cb(isFinite(lat) && isFinite(lng) ? { lat: lat, lng: lng } : null);
      }).catch(function () { cb(null); });
  }

  // ZIP → coordinates. Prefers the Maps JavaScript geocoder (works with HTTP-referrer
  // restricted keys), then the Google REST endpoint (when a key is set), then the
  // keyless zippopotam fallback so ZIP distance search works with no API key.
  // Callers handle a null result by showing all eligible centers. No PHI (ZIP only).
  function geocodeZip(zip, cb) {
    if (!/^\d{5}$/.test(zip)) { cb(null); return; }
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      try {
        new window.google.maps.Geocoder().geocode(
          { address: zip, componentRestrictions: { country: 'US' } },
          function (res, status) {
            if (status === 'OK' && res && res[0]) {
              var l = res[0].geometry.location;
              cb({ lat: l.lat(), lng: l.lng() });
            } else { zipCentroid(zip, cb); }
          }
        );
        return;
      } catch (e) { /* fall through */ }
    }
    if (window.RSC_MAPS_KEY) {
      fetch('https://maps.googleapis.com/maps/api/geocode/json?components=postal_code:' +
        encodeURIComponent(zip) + '&key=' + encodeURIComponent(window.RSC_MAPS_KEY))
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var loc = d.results && d.results[0] && d.results[0].geometry && d.results[0].geometry.location;
          if (loc) cb({ lat: loc.lat, lng: loc.lng }); else zipCentroid(zip, cb);
        }).catch(function () { zipCentroid(zip, cb); });
      return;
    }
    zipCentroid(zip, cb);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
    });
  }

  window.RSC = {
    haversine: haversine,
    byService: byService,
    sortByGeo: sortByGeo,
    textFilter: textFilter,
    withinRadius: withinRadius,
    brandDisplay: brandDisplay,
    getSelected: getSelected,
    setSelected: setSelected,
    clearSelected: clearSelected,
    geolocate: geolocate,
    geocodeZip: geocodeZip,
    esc: esc,
    on: function (fn) { document.addEventListener('rsc:center-changed', function (e) { fn(e.detail); }); }
  };
  // `centers` is a live getter (not a one-time snapshot) so it reflects the blob even
  // when that loads after this module - see resolveCenters() above.
  Object.defineProperty(window.RSC, 'centers', { get: resolveCenters, enumerable: true });
})();
